import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Card, CardContent } from 'components/ui/card'
import { SiteAvatar } from 'components/SiteAvatar'
import { fetchSiteFinancials } from 'slices/siteFinancialSlice'
import FinancialModelSection from './FinancialModelSection'

interface FinancialSiteCardProps {
	evalId: string
	site: any
	/** Letter badge (A, B, C, …) shown when the site has no linked Location image. */
	letter: string
}

const MODEL_DESCRIPTIONS: { key: 'acquisition' | 'operating' | 'roi'; label: string; description: string }[] = [
	{
		key: 'acquisition',
		label: 'Acquisition',
		description: 'Purchase price, TI, closing costs, fees.',
	},
	{ key: 'operating', label: 'Operating', description: 'Rent, CAM, utilities, ongoing expenses.' },
	{ key: 'roi', label: 'ROI', description: 'Investment, projected revenue, return metrics.' },
]

/**
 * One site's full financial picture (Acquisition + Operating + ROI stacked).
 * Fetches all three models for its site on mount; each section owns its own
 * edit state and save button so edits on one section don't affect others.
 */
const FinancialSiteCard = ({ evalId, site, letter }: FinancialSiteCardProps) => {
	const dispatch = useDispatch()
	const siteId = site._id

	useEffect(() => {
		// @ts-ignore
		dispatch(fetchSiteFinancials({ evalId, siteId }))
	}, [dispatch, evalId, siteId])

	const linkedLocationName = site.location?.assetName

	return (
		<Card className="min-w-[340px] max-w-[400px] flex-shrink-0 flex flex-col">
			<div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100">
				<SiteAvatar
					imageUrl={site.location?.image}
					letter={letter}
					size="sm"
				/>
				<div className="min-w-0 flex-1">
					<h4 className="font-semibold text-sm text-gray-900 truncate">{site.name}</h4>
					{linkedLocationName && (
						<p className="text-xs text-gray-500 truncate">{linkedLocationName}</p>
					)}
				</div>
			</div>

			<CardContent className="p-4 space-y-4 overflow-y-auto max-h-[640px]">
				{MODEL_DESCRIPTIONS.map((m, idx) => (
					<div key={m.key} className={idx > 0 ? 'pt-4 border-t border-gray-100' : ''}>
						<FinancialModelSection
							evalId={evalId}
							siteId={siteId}
							modelType={m.key}
							label={m.label}
							description={m.description}
						/>
					</div>
				))}
			</CardContent>
		</Card>
	)
}

export default FinancialSiteCard
